// Iter 275h — Live mini-ticker rendered on every locked pick row in
// the Schedule sub-views (Top 20 Picks, x5 Odds DC+HT, Easy Wins).
//
// Anatomy of the ticker when a fixture is in-play:
//
//   [ 🔴 62' ]  [ 0-1 ]  [ 🟥 -1 ]  [ WINNING ]
//         ↑              ↑            ↑
//     minute pill   score pill    pick status
//                  (jumps to      (green/rose/
//                  /live-ai)      amber)
//
// One shared poll of `/api/live/snapshot` at 25s (via `useLiveSnapshot`)
// feeds every ticker on the page, so the badge count stays predictable
// regardless of how many rows are rendered.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 25 * 1000;

// ── Shared snapshot hook ────────────────────────────────────────────
// Module-scoped cache so every ticker instance shares one poller.

let _shared = {
  fixtures: {},
  updatedAt: 0,
  listeners: new Set(),
  poller: null,
};

const _startPoller = () => {
  if (_shared.poller) return;
  const tick = async () => {
    try {
      const r = await fetch(`${API_URL}/api/live/snapshot`);
      if (r.ok) {
        const d = await r.json();
        _shared.fixtures = d?.fixtures || {};
        _shared.updatedAt = Date.now();
        _shared.listeners.forEach((fn) => fn(_shared.fixtures));
      }
    } catch {
      /* transient network — next tick retries */
    }
  };
  tick();
  _shared.poller = setInterval(tick, POLL_INTERVAL_MS);
};

const _stopPollerIfIdle = () => {
  if (_shared.listeners.size === 0 && _shared.poller) {
    clearInterval(_shared.poller);
    _shared.poller = null;
  }
};

const useLiveSnapshot = () => {
  const [snap, setSnap] = useState(_shared.fixtures);
  useEffect(() => {
    const fn = (fx) => setSnap({ ...fx });
    _shared.listeners.add(fn);
    _startPoller();
    return () => {
      _shared.listeners.delete(fn);
      _stopPollerIfIdle();
    };
  }, []);
  return snap;
};

// ── Pick outcome inference (in-play) ────────────────────────────────
// Given the market/prediction and the live score, tell whether the
// pick is currently winning, losing, or still pending resolution.
// Deliberately conservative — when we can't compute a definitive
// verdict we return 'pending' so the UI doesn't lie.

const _statusIsFT = (s) => s === "FT" || s === "AET" || s === "PEN";
const _statusIsHT = (s) => s === "HT" || s === "P";  // P = penalties
const _pastHT = (s) => s === "2H" || _statusIsHT(s) || _statusIsFT(s);

const computeLivePickStatus = (marketType, prediction, snap) => {
  if (!snap) return null;
  const h = Number(snap.home_score || 0);
  const a = Number(snap.away_score || 0);
  const st = snap.status;
  const t = h + a;
  const mt = (marketType || "").toLowerCase();
  const pd = (prediction || "").toLowerCase();

  const won = () => ({ label: "Won", tone: "emerald" });
  const lost = () => ({ label: "Lost", tone: "rose" });
  const winning = () => ({ label: "Winning", tone: "emerald" });
  const losing = () => ({ label: "Losing", tone: "rose" });
  const pending = (l = "Pending") => ({ label: l, tone: "slate" });

  // ── HT markets ──────────────────────────────────────────────────
  if (mt.includes("ht btts") || mt.includes("halftime btts")) {
    const bothScored = h > 0 && a > 0;
    if (pd.startsWith("yes")) {
      if (bothScored) return _pastHT(st) ? won() : winning();
      return _pastHT(st) ? lost() : (h + a === 0 ? winning() : losing());
    }
    if (pd.startsWith("no")) {
      if (bothScored) return _pastHT(st) ? lost() : losing();
      return _pastHT(st) ? won() : winning();
    }
  }
  if (mt.includes("ht o/u") || mt.includes("halftime o/u") || mt.includes("halftime over") || mt.includes("halftime under")) {
    const lineMatch = (mt + " " + pd).match(/(\d+\.\d)/);
    const line = lineMatch ? parseFloat(lineMatch[1]) : 0.5;
    if (pd.startsWith("over")) {
      if (t > line) return _pastHT(st) ? won() : winning();
      return _pastHT(st) ? lost() : (t === 0 ? losing() : pending());
    }
    if (pd.startsWith("under")) {
      if (t > line) return _pastHT(st) ? lost() : losing();
      return _pastHT(st) ? won() : winning();
    }
  }

  // ── Double Chance ───────────────────────────────────────────────
  if (mt.includes("double chance")) {
    const leader = h > a ? "home" : (a > h ? "away" : "draw");
    if (pd.includes("home") && pd.includes("draw")) {
      // 1X = Home or Draw
      if (leader === "away") return _statusIsFT(st) ? lost() : losing();
      return _statusIsFT(st) ? won() : winning();
    }
    if (pd.includes("away") && pd.includes("draw")) {
      // X2 = Away or Draw
      if (leader === "home") return _statusIsFT(st) ? lost() : losing();
      return _statusIsFT(st) ? won() : winning();
    }
    if (pd.includes("home") && pd.includes("away")) {
      // 12 = No Draw
      if (leader === "draw") return _statusIsFT(st) ? lost() : losing();
      return _statusIsFT(st) ? won() : winning();
    }
  }

  // ── 1X2 ─────────────────────────────────────────────────────────
  if (mt === "1x2" || mt.includes("match result") || mt.includes("full time result")) {
    const leader = h > a ? "home" : (a > h ? "away" : "draw");
    if (pd.startsWith("home")) return leader === "home" ? (_statusIsFT(st) ? won() : winning()) : (_statusIsFT(st) ? lost() : losing());
    if (pd.startsWith("away")) return leader === "away" ? (_statusIsFT(st) ? won() : winning()) : (_statusIsFT(st) ? lost() : losing());
    if (pd.startsWith("draw")) return leader === "draw" ? (_statusIsFT(st) ? won() : winning()) : (_statusIsFT(st) ? lost() : losing());
  }

  // ── BTTS ────────────────────────────────────────────────────────
  if (mt === "btts" || mt.includes("both teams to score")) {
    const both = h > 0 && a > 0;
    if (pd.startsWith("yes")) return both ? (_statusIsFT(st) ? won() : winning()) : (_statusIsFT(st) ? lost() : (h + a === 0 ? winning() : pending()));
    if (pd.startsWith("no"))  return both ? (_statusIsFT(st) ? lost() : losing()) : (_statusIsFT(st) ? won() : winning());
  }

  // ── Over/Under (full-time totals) ───────────────────────────────
  const ouMatch = (mt + " " + pd).match(/(over|under)\s*(\d+\.\d)/);
  if (ouMatch) {
    const dir = ouMatch[1];
    const line = parseFloat(ouMatch[2]);
    if (dir === "over") {
      if (t > line) return _statusIsFT(st) ? won() : winning();
      return _statusIsFT(st) ? lost() : (line - t > 2 ? losing() : pending());
    }
    if (dir === "under") {
      if (t > line) return _statusIsFT(st) ? lost() : losing();
      return _statusIsFT(st) ? won() : winning();
    }
  }

  return pending();
};

// ── The ticker component ────────────────────────────────────────────

export const LivePickTracker = ({
  fixtureId,
  matchId,
  marketType,
  prediction,
  compact = false,
  testidPrefix = "live-tracker",
}) => {
  const snap = useLiveSnapshot();
  const fx = snap && fixtureId ? snap[String(fixtureId)] : null;
  const navigate = useNavigate();

  // Nothing to show when the fixture isn't live.
  if (!fx) return null;

  const st = fx.status;
  const isLive = ["1H", "2H", "HT", "ET", "P", "BT", "LIVE"].includes(st);
  const isFT = ["FT", "AET", "PEN"].includes(st);
  if (!isLive && !isFT) return null;

  const status = computeLivePickStatus(marketType, prediction, fx);
  const minute = fx.minute != null ? `${fx.minute}${fx.minute_extra ? `+${fx.minute_extra}` : ""}'` : (st === "HT" ? "HT" : (isFT ? "FT" : ""));
  const scoreStr = fx.score_str || `${fx.home_score ?? 0}-${fx.away_score ?? 0}`;
  const redCards = (fx.home_red_cards || 0) + (fx.away_red_cards || 0);

  const toneMap = {
    emerald: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
    rose: "bg-rose-500/20 text-rose-200 border-rose-500/40",
    slate: "bg-slate-500/15 text-slate-200 border-slate-500/30",
  };

  const openLive = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/live-ai/${fixtureId}`);
  };

  return (
    <div
      className="w-full mt-1.5 flex items-center gap-2 rounded-md border border-rose-500/40 bg-gradient-to-r from-rose-500/[0.08] via-rose-500/[0.04] to-transparent px-2 py-1 shadow-[0_0_16px_-6px_rgba(244,63,94,0.6)]"
      data-testid={`${testidPrefix}-${fixtureId}`}
    >
      {/* LIVE prefix */}
      <span className="text-[9px] font-black uppercase tracking-[0.18em] text-rose-300 shrink-0">
        {isLive ? "● LIVE" : "FT"}
      </span>

      {/* Minute pill — red dot pulses while live */}
      <span
        className={[
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-mono tabular-nums font-bold",
          isLive
            ? "border-rose-500/70 bg-rose-500/20 text-rose-100"
            : "border-slate-500/40 bg-slate-500/15 text-slate-200",
        ].join(" ")}
        title={isLive ? "In play" : "Final"}
      >
        {isLive && <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />}
        {minute || (isFT ? "FT" : "•")}
      </span>

      {/* Score — its own click target that opens /live-ai for this fixture.
          Uses <span role="button"> instead of <button> because the surrounding
          row is already a <button data-testid="{tab}-pick-{i}">; nested
          <button> elements trigger a React hydration warning and are invalid
          HTML.  Handles Enter/Space keyboard activation manually. */}
      <span
        role="button"
        tabIndex={0}
        onClick={openLive}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") openLive(e);
        }}
        className="inline-flex items-center gap-1 rounded-full border-2 border-cyan-400/60 bg-black/40 px-2.5 py-0.5 text-sm font-mono tabular-nums font-black text-white hover:border-cyan-300 hover:bg-cyan-500/20 hover:text-cyan-100 transition shadow-[0_0_12px_-4px_rgba(34,211,238,0.5)] cursor-pointer select-none"
        data-testid={`${testidPrefix}-score-${fixtureId}`}
        title="Open live view"
      >
        {scoreStr}
        <span className="text-[8px] text-cyan-300 opacity-80">↗</span>
      </span>

      {/* Red card notch */}
      {redCards > 0 && (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-rose-500/50 bg-rose-600/25 px-1.5 py-0.5 text-[10px] font-bold text-rose-100"
          title={`${redCards} red card${redCards > 1 ? "s" : ""}`}
        >
          <span className="inline-block w-1.5 h-2.5 bg-rose-500 rounded-sm" />
          {redCards}
        </span>
      )}

      {/* Pick's live status */}
      {status && !compact && (
        <span
          className={[
            "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ml-auto",
            toneMap[status.tone] || toneMap.slate,
          ].join(" ")}
          data-testid={`${testidPrefix}-pick-status-${fixtureId}`}
        >
          {status.label}
        </span>
      )}
    </div>
  );
};

export default LivePickTracker;
